import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import moment from 'moment';
import 'moment/locale/th';
import { SERVICE_URL } from '../../utils/api';
import shrinkImage from './shrinkImage';
import { getCleanerToken, NO_TOKEN_MESSAGE } from './cleanerToken';

// หน้าลงเวลาเข้า-ออกงาน (ลิงก์ส่วนตัวของแม่บ้าน /once/cleaner/clock?t=) -- docs/cleaner-attendance-plan.md (API)
// ขั้นตอน: ถ่ายเซลฟี่ (ถ้าที่พักบังคับ) -> กดปุ่ม -> ขอ GPS -> ส่ง

const getLocation = () => new Promise((resolve, reject) => {
  if (!navigator.geolocation) return reject(new Error('เครื่องนี้ไม่รองรับ GPS'));
  navigator.geolocation.getCurrentPosition(
    (p) => resolve(p.coords),
    (e) => reject(new Error(e.code === 1
      ? 'กรุณาอนุญาตให้ LINE / เบราว์เซอร์ เข้าถึงตำแหน่ง (Location) แล้วลองใหม่'
      : 'หาตำแหน่งไม่ได้ ลองออกไปที่โล่งแล้วกดใหม่')),
    { enableHighAccuracy: true, timeout: 20000, maximumAge: 0 },
  );
});

const errMsg = (e) => e?.response?.data?.message || e?.message || 'เกิดข้อผิดพลาด ลองใหม่อีกครั้ง';

export default function CleanerClock() {
  const token = getCleanerToken(); // ?t= หรือรหัสที่เคยเปิดไว้ในเครื่องนี้
  const [status, setStatus] = useState(null);
  const [error, setError] = useState(null);
  const [now, setNow] = useState(new Date());
  const [photoUrl, setPhotoUrl] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null); // { ok, text }
  const fileRef = useRef(null);
  // เช็คลิสต์รายวัน (เฉพาะผู้ช่วยรายวัน) -- ชุดเดียวต่อที่พักต่อวัน ผู้ช่วยช่วยกันติ๊ก
  const [checklist, setChecklist] = useState(null); // { groups, done, total }
  const [checkBusy, setCheckBusy] = useState(null); // id ที่กำลังบันทึก
  const checkFileRef = useRef(null);
  const photoFor = useRef(null); // ข้อที่กำลังถ่ายรูป

  const cw = useCallback((path, opts = {}) => {
    const sep = path.includes('?') ? '&' : '?';
    return axios({ baseURL: SERVICE_URL, url: `cleaner-web/${path}${sep}t=${encodeURIComponent(token || '')}`, ...opts });
  }, [token]);

  const load = useCallback(() => {
    if (!token) { setError(NO_TOKEN_MESSAGE); return; }
    cw('attendance').then((r) => setStatus(r.data)).catch((e) => setError(errMsg(e)));
  }, [cw, token]);

  useEffect(() => { moment.locale('th'); load(); }, [load]);

  const loadChecklist = useCallback(() => {
    cw('attendance/checklist').then((r) => setChecklist(r.data)).catch(() => setChecklist(null));
  }, [cw]);
  useEffect(() => { if (status && status.checklist) loadChecklist(); }, [status && status.checklist, loadChecklist]); // eslint-disable-line react-hooks/exhaustive-deps

  // อัปเดตข้อในหน้าจอทันที แล้วนับใหม่
  const applyCheck = (row) => setChecklist((cl) => {
    if (!cl) return cl;
    const groups = cl.groups.map((g) => {
      const items = g.items.map((i) => (i.id === row.id ? { ...i, ...row, done_by_name: row.done ? (status?.cleaner?.firstname || i.done_by_name) : null } : i));
      return { ...g, items, done: items.filter((i) => i.done).length };
    });
    const all = groups.flatMap((g) => g.items);
    return { ...cl, groups, done: all.filter((i) => i.done).length };
  });
  const saveCheck = async (item, patch) => {
    setCheckBusy(item.id); setMsg(null);
    try {
      const r = await cw(`attendance/checklist/${item.id}`, { method: 'post', data: patch });
      applyCheck(r.data);
    } catch (e) {
      setMsg({ ok: false, text: errMsg(e) });
    }
    setCheckBusy(null);
  };
  const toggleCheck = (item) => {
    if (!item.done && item.require_photo && !(item.photo_urls || []).length) {
      photoFor.current = item; if (checkFileRef.current) checkFileRef.current.click();
      return;
    }
    saveCheck(item, { done: !item.done });
  };
  const onCheckPhoto = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = '';
    const item = photoFor.current;
    if (!file || !item) return;
    setCheckBusy(item.id); setMsg(null);
    try {
      const small = await shrinkImage(file, 1280, 0.75);
      const fd = new FormData();
      fd.append('file', small, 'checklist.jpg');
      const up = await cw('upload-photo', { method: 'post', data: fd, timeout: 90000 });
      const photos = [...(item.photo_urls || []), up.data.url].slice(-5);
      await saveCheck(item, { photo_urls: photos, done: true }); // ถ่ายรูปแล้ว = ทำข้อนี้แล้ว
    } catch (err) {
      setMsg({ ok: false, text: 'ส่งรูปไม่สำเร็จ สัญญาณเน็ตอาจไม่ดี ลองถ่ายใหม่' });
      setCheckBusy(null);
    }
  };
  const addPhoto = (item) => { photoFor.current = item; if (checkFileRef.current) checkFileRef.current.click(); };
  useEffect(() => { const t = setInterval(() => setNow(new Date()), 1000); return () => clearInterval(t); }, []);

  const onPhoto = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = '';
    if (!file) return;
    setUploading(true); setMsg(null);
    try {
      const small = await shrinkImage(file, 1024, 0.75);
      const fd = new FormData();
      fd.append('file', small, 'selfie.jpg');
      const r = await cw('upload-photo', { method: 'post', data: fd, timeout: 90000 });
      setPhotoUrl(r.data.url);
    } catch (err) {
      setMsg({ ok: false, text: 'ส่งรูปไม่สำเร็จ สัญญาณเน็ตอาจไม่ดี ลองถ่ายใหม่' });
    }
    setUploading(false);
  };

  const submit = async () => {
    if (!status) return;
    if (status.require_photo && !photoUrl) { setMsg({ ok: false, text: 'กรุณาถ่ายรูปก่อน' }); return; }
    const type = status.clocked_in ? 'out' : 'in';
    const left = checklist && checklist.total ? checklist.total - checklist.done : 0;
    const confirmText = left > 0
      ? `เช็คลิสต์วันนี้ยังไม่ครบ (ทำแล้ว ${checklist.done}/${checklist.total})\nยืนยันเช็คเอาท์ (เลิกงาน)?`
      : 'ยืนยันเช็คเอาท์ (เลิกงาน)?';
    if (type === 'out' && !window.confirm(confirmText)) return;
    setBusy(true); setMsg(null);
    try {
      const c = await getLocation();
      const r = await cw('attendance/clock', {
        method: 'post',
        data: { type, lat: c.latitude, lng: c.longitude, accuracy: c.accuracy, photo_url: photoUrl },
      });
      setStatus((s) => ({ ...s, ...r.data.status }));
      setPhotoUrl(null);
      setMsg({ ok: true, text: `${type === 'in' ? 'เช็คอิน' : 'เช็คเอาท์'}เรียบร้อย ${moment(r.data.log.at).format('HH:mm')} น.` });
    } catch (e) {
      setMsg({ ok: false, text: errMsg(e) });
    }
    setBusy(false);
  };

  // ---------- styles (โทนเดียวกับ CleanerWork) ----------
  const S = {
    page: { boxSizing: 'border-box', width: '100%', maxWidth: 480, margin: '0 auto', paddingTop: 76, paddingRight: 16, paddingBottom: 48, paddingLeft: 16, color: '#1A211D', minHeight: '100vh' },
    h: { fontSize: 20, fontWeight: 800, margin: 0 },
    sub: { fontSize: 13, color: '#586159' },
    card: { boxSizing: 'border-box', border: '1.5px solid #DBE2DC', borderRadius: 14, padding: 16, marginBottom: 12, background: '#fff' },
    bigBtn: (bg) => ({ boxSizing: 'border-box', width: '100%', border: 'none', borderRadius: 16, padding: 20, fontSize: 22, fontWeight: 800, color: '#fff', background: bg, opacity: busy ? 0.6 : 1 }),
    ghost: { boxSizing: 'border-box', width: '100%', border: '1.5px dashed #CBD4CC', borderRadius: 14, padding: 14, fontSize: 16, fontWeight: 700, background: '#fff', color: '#1A211D' },
    msg: (ok) => ({ borderRadius: 12, padding: '12px 14px', marginBottom: 12, fontWeight: 700, color: ok ? '#0F5D3B' : '#A12C23', background: ok ? '#E7F4EC' : '#FBEAE8' }),
  };

  if (error) {
    return <div style={{ ...S.page, textAlign: 'center', paddingTop: 80 }}>
      <div style={{ fontSize: 40 }}>🔒</div>
      <p style={{ marginTop: 12, color: '#586159' }}>{error}</p>
    </div>;
  }
  if (!status) return <div style={{ ...S.page, textAlign: 'center', paddingTop: 80, color: '#586159' }}>กำลังโหลด…</div>;

  if (!status.enabled) {
    return <div style={{ ...S.page, textAlign: 'center', paddingTop: 80 }}>
      <div style={{ fontSize: 40 }}>🕒</div>
      <p style={{ marginTop: 12, color: '#586159' }}>ที่พักนี้ยังไม่เปิดระบบลงเวลา กรุณาแจ้งแอดมิน</p>
    </div>;
  }

  const inColor = '#0F8A56', outColor = '#C2410C';
  const todayLogs = (status.recent || []).filter((l) => moment(l.at).isSame(moment(), 'day'));

  return (
    <div style={S.page}>
      <div style={{ marginBottom: 12 }}>
        <h1 style={S.h}>สวัสดีค่ะ พี่{status.cleaner.firstname} 🕒</h1>
        <div style={S.sub}>ลงเวลาเข้า-ออกงาน · {status.hotel?.name || ''}</div>
      </div>

      {msg && <div style={S.msg(msg.ok)}>{msg.text}</div>}

      <div style={{ ...S.card, textAlign: 'center' }}>
        <div style={{ fontSize: 48, fontWeight: 800, fontVariantNumeric: 'tabular-nums' }}>{moment(now).format('HH:mm:ss')}</div>
        <div style={S.sub}>{moment(now).format('dddd D MMMM YYYY')}</div>
        <div style={{ marginTop: 10, fontWeight: 700, color: status.clocked_in ? inColor : '#8A938B' }}>
          {status.clocked_in ? `● กำลังทำงาน (เข้า ${moment(status.since).format(moment(status.since).isSame(moment(), 'day') ? 'HH:mm' : 'D MMM HH:mm')} น.)` : '○ ยังไม่ได้เช็คอิน'}
        </div>
      </div>

      {status.require_photo && (
        <div style={S.card}>
          <input ref={fileRef} type="file" accept="image/*" capture="user" onChange={onPhoto} style={{ display: 'none' }} />
          <button type="button" style={S.ghost} disabled={uploading || busy} onClick={() => fileRef.current && fileRef.current.click()}>
            {uploading ? 'กำลังส่งรูป…' : photoUrl ? '📷 ถ่ายใหม่' : '📷 ถ่ายเซลฟี่ยืนยันตัวตน'}
          </button>
          {photoUrl && <img src={photoUrl} alt="รูปที่ถ่าย" style={{ width: '100%', borderRadius: 12, marginTop: 10 }} />}
        </div>
      )}

      <button type="button" style={S.bigBtn(status.clocked_in ? outColor : inColor)} disabled={busy || uploading} onClick={submit}>
        {busy ? 'กำลังบันทึก…' : status.clocked_in ? 'เช็คเอาท์ (เลิกงาน)' : 'เช็คอิน (เข้างาน)'}
      </button>
      <div style={{ ...S.sub, textAlign: 'center', marginTop: 8 }}>ต้องอยู่ในพื้นที่ที่พัก (รัศมี {status.radius_m} ม.) และเปิด GPS</div>

      {status.checklist && checklist && checklist.total > 0 && (
        <div style={{ ...S.card, marginTop: 16 }}>
          <input ref={checkFileRef} type="file" accept="image/*" capture="environment" onChange={onCheckPhoto} style={{ display: 'none' }} />
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
            <div style={{ fontWeight: 800, fontSize: 17 }}>📋 เช็คลิสต์วันนี้</div>
            <div style={{ fontWeight: 800, color: checklist.done >= checklist.total ? inColor : '#586159' }}>{checklist.done}/{checklist.total}</div>
          </div>
          <div style={{ ...S.sub, marginBottom: 8 }}>ทุกคนช่วยกันติ๊กชุดเดียวกัน · เวลาเป็นแนวทาง</div>
          {checklist.groups.map((g) => (
            <div key={g.key} style={{ borderTop: '1px solid #EEF1EE', paddingTop: 10, marginTop: 6 }}>
              <div style={{ fontWeight: 800 }}>{g.title}</div>
              <div style={{ ...S.sub, marginBottom: 6 }}>
                {g.time ? `🕐 ${g.time}` : ''}
                {g.room_name ? `${g.time ? ' · ' : ''}🏠 ${g.room_name}${g.reason === 'checkout' ? ' (เช็คเอาท์)' : g.reason === 'checkin' ? ' (เช็คอิน)' : g.reason === 'stay' ? ' (พักต่อ)' : ''}` : ''}
                {` · ${g.done}/${g.total}`}
              </div>
              {g.items.map((i) => (
                <div key={i.id} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', padding: '8px 0' }}>
                  <button type="button" disabled={checkBusy === i.id} onClick={() => toggleCheck(i)}
                    style={{ flex: 'none', width: 30, height: 30, borderRadius: 8, border: `2px solid ${i.done ? inColor : '#B8C2BA'}`, background: i.done ? inColor : '#fff', color: '#fff', fontSize: 18, fontWeight: 800, lineHeight: '24px', padding: 0 }}>
                    {i.done ? '✓' : ''}
                  </button>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 15, color: i.done ? '#586159' : '#1A211D', textDecoration: i.done ? 'line-through' : 'none' }}>{i.label}</div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center', marginTop: 4 }}>
                      {i.require_photo && !(i.photo_urls || []).length && <span style={{ fontSize: 12, color: '#B45309', fontWeight: 700 }}>📷 ต้องถ่ายรูป</span>}
                      {(i.photo_urls || []).map((u) => <img key={u} src={u} alt="" style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 6 }} />)}
                      <button type="button" disabled={checkBusy === i.id} onClick={() => addPhoto(i)} style={{ border: '1px solid #CBD4CC', background: '#fff', borderRadius: 8, padding: '2px 8px', fontSize: 12 }}>
                        {checkBusy === i.id ? 'กำลังบันทึก…' : '📷 ถ่ายรูป'}
                      </button>
                      {i.done && i.done_by_name && <span style={{ fontSize: 12, color: '#8A938B' }}>✓ {i.done_by_name}</span>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>
      )}

      {todayLogs.length > 0 && (
        <div style={{ ...S.card, marginTop: 16 }}>
          <div style={{ fontWeight: 800, marginBottom: 6 }}>วันนี้</div>
          {todayLogs.map((l) => (
            <div key={l.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderTop: '1px solid #EEF1EE' }}>
              <span style={{ color: l.type === 'in' ? inColor : outColor, fontWeight: 700 }}>{l.type === 'in' ? 'เข้า' : 'ออก'}{l.source === 'admin' ? ' (แอดมินบันทึก)' : ''}</span>
              <span style={{ fontVariantNumeric: 'tabular-nums' }}>{moment(l.at).format('HH:mm')} น.</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
